import type { Activity } from "@cva/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { orpc } from "./api";
import { onUploadFinished, type UploadItem, useUploads } from "./uploads";

/**
 * What happens in the background with the user's files: uploads (browser)
 * and the work the server does afterwards (preview, speech recognition,
 * exports), grouped per file.
 */

export type TaskStatus = "queued" | "running" | "done" | "failed";

export type Task =
  | { id: string; kind: "upload"; status: TaskStatus; upload: UploadItem }
  | { id: string; kind: Activity["kind"]; status: TaskStatus; activity: Activity };

export interface ActivityGroup {
  key: string;
  title: string;
  assetId: string | null;
  mine: boolean;
  tasks: Task[];
  /** Running or waiting, failed (and nothing running) or done. */
  status: TaskStatus;
  updatedAt: number;
}

const uploadStatus = (item: UploadItem): TaskStatus =>
  item.status === "uploading"
    ? "running"
    : item.status === "done"
      ? "done"
      : item.status === "error"
        ? "failed"
        : "done";

const ORDER: Record<Task["kind"], number> = {
  upload: 0,
  media: 1,
  cutout: 2,
  matte: 3,
  transcript: 4,
  export: 5,
};

function groupStatus(tasks: Task[]): TaskStatus {
  if (tasks.some((task) => task.status === "running")) return "running";
  if (tasks.some((task) => task.status === "queued")) return "queued";
  if (tasks.some((task) => task.status === "failed")) return "failed";
  return "done";
}

export const isActive = (status: TaskStatus) => status === "running" || status === "queued";

function buildGroups(uploads: UploadItem[], activity: Activity[]): ActivityGroup[] {
  const groups = new Map<string, ActivityGroup>();
  const group = (key: string, title: string, assetId: string | null, mine: boolean) => {
    let entry = groups.get(key);
    if (!entry) {
      entry = { key, title, assetId, mine, tasks: [], status: "done", updatedAt: 0 };
      groups.set(key, entry);
    }
    entry.mine ||= mine;
    return entry;
  };
  for (const upload of uploads) {
    const entry = group(
      upload.assetId ? `asset:${upload.assetId}` : `upload:${upload.id}`,
      upload.name,
      upload.assetId ?? null,
      true,
    );
    entry.tasks.push({
      id: `upload:${upload.id}`,
      kind: "upload",
      status: uploadStatus(upload),
      upload,
    });
    entry.updatedAt = Math.max(entry.updatedAt, upload.finishedAt ?? upload.startedAt);
  }
  for (const item of activity) {
    const entry = group(
      item.assetId ? `asset:${item.assetId}` : item.id,
      item.title,
      item.assetId,
      item.mine,
    );
    // The media library name (the upload only knows the file name).
    if (item.assetId) entry.title = item.title;
    entry.tasks.push({ id: item.id, kind: item.kind, status: item.status, activity: item });
    entry.updatedAt = Math.max(entry.updatedAt, new Date(item.updatedAt).getTime());
  }
  const list = [...groups.values()];
  for (const entry of list) {
    entry.tasks.sort((a, b) => ORDER[a.kind] - ORDER[b.kind]);
    entry.status = groupStatus(entry.tasks);
  }
  // Running first, then the newest.
  return list.sort(
    (a, b) => Number(isActive(b.status)) - Number(isActive(a.status)) || b.updatedAt - a.updatedAt,
  );
}

// ---------------------------------------------------------------------------
// Dismissed groups (per browser tab)
// ---------------------------------------------------------------------------

const DISMISSED_KEY = "cva.activity.dismissed";
let dismissed = new Map<string, number>(readDismissed());
const dismissListeners = new Set<() => void>();

function readDismissed(): [string, number][] {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISSED_KEY) ?? "[]") as [string, number][];
  } catch {
    return [];
  }
}

/** Hides finished groups until something new happens with them. */
export function dismissGroups(groups: ActivityGroup[]) {
  dismissed = new Map(dismissed);
  for (const group of groups) dismissed.set(group.key, group.updatedAt);
  try {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...dismissed].slice(-200)));
  } catch {
    // private mode: dismissed for this page only
  }
  for (const listener of dismissListeners) listener();
}

function useDismissed() {
  return useSyncExternalStore(
    (listener) => {
      dismissListeners.add(listener);
      return () => dismissListeners.delete(listener);
    },
    () => dismissed,
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Polls fast while something runs and for a moment after (the next step may follow). */
const FAST = 2500;
const SLOW = 30_000;
const LINGER = 20_000;

export function useActivity(orgId: string | null) {
  const allUploads = useUploads();
  const uploads = useMemo(
    () => allUploads.filter((item) => item.orgId === orgId),
    [allUploads, orgId],
  );
  const queryClient = useQueryClient();
  const busyUntil = useRef(0);
  const uploading = uploads.some((item) => item.status === "uploading");
  const query = useQuery({
    ...orpc.activity.list.queryOptions({ input: { orgId: orgId ?? "" } }),
    enabled: Boolean(orgId),
    refetchInterval: (current) => {
      const busy = uploading || (current.state.data ?? []).some((item) => isActive(item.status));
      if (busy) busyUntil.current = Date.now() + LINGER;
      return Date.now() < busyUntil.current ? FAST : SLOW;
    },
  });
  useEffect(
    () =>
      onUploadFinished(() => {
        busyUntil.current = Date.now() + LINGER;
        void queryClient.invalidateQueries({ queryKey: orpc.activity.key() });
      }),
    [queryClient],
  );
  // A finished transcription: the editor loads the words right away.
  // A finished cut-out: it shows up in the media library panels; a finished
  // person cut-out: the editor plays the video with it.
  const seenDone = useRef(new Set<string>());
  useEffect(() => {
    for (const item of query.data ?? []) {
      if (item.status !== "done" || !item.assetId || !orgId) continue;
      if (item.kind !== "transcript" && item.kind !== "cutout" && item.kind !== "matte") continue;
      const key = `${item.id}:${new Date(item.updatedAt).getTime()}`;
      if (seenDone.current.has(key)) continue;
      seenDone.current.add(key);
      void queryClient.invalidateQueries({
        queryKey:
          item.kind === "transcript"
            ? orpc.media.transcript.queryKey({ input: { orgId, assetId: item.assetId } })
            : orpc.media.key(),
      });
    }
  }, [query.data, orgId, queryClient]);
  const hidden = useDismissed();
  const groups = useMemo(
    () =>
      buildGroups(uploads, query.data ?? []).filter((group) => {
        const at = hidden.get(group.key);
        return at === undefined || isActive(group.status) || group.updatedAt > at;
      }),
    [uploads, query.data, hidden],
  );
  return groups;
}

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------

/** Current time, updated every second while `active`. */
export function useNow(active: boolean): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

/**
 * Running time of a server task, counted on locally between two polls. The
 * server sends how long the task has run, so a wrong clock on the user's
 * computer does not matter.
 */
export function useElapsed(serverMs: number | null): number | null {
  const base = useRef<{ value: number; at: number } | null>(null);
  if (serverMs === null) base.current = null;
  else if (base.current?.value !== serverMs) base.current = { value: serverMs, at: Date.now() };
  const now = useNow(serverMs !== null);
  return base.current ? base.current.value + Math.max(0, now - base.current.at) : null;
}

/**
 * Progress between the coarse steps whisper reports (one per 30 s window):
 * by time, from the expected duration, but never ahead of 95 % and never
 * behind what the server reported.
 */
export function estimateProgress(
  reported: number,
  elapsedMs: number | null,
  estimatedMs: number | null,
): { progress: number; remainingMs: number | null } {
  if (elapsedMs === null || !estimatedMs) return { progress: reported, remainingMs: null };
  const byTime = Math.min(95, (elapsedMs / estimatedMs) * 100);
  const progress = Math.max(reported, byTime);
  const remainingMs =
    progress > byTime + 1 ? (elapsedMs * (100 - progress)) / progress : estimatedMs - elapsedMs;
  return { progress, remainingMs };
}

/** "1:05" */
export function formatClock(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 60)
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")} h`;
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

/** "noch ca. 2 Min." */
export function formatRemaining(ms: number): string {
  if (ms < 10_000) return "gleich fertig";
  if (ms < 60_000) return `noch ca. ${Math.round(ms / 5000) * 5} s`;
  if (ms < 90 * 60_000) return `noch ca. ${Math.round(ms / 60_000)} Min.`;
  return `noch ca. ${Math.round(ms / 3_600_000)} Std.`;
}

/** Upload speed (bytes/s) and remaining time, once there is enough to measure. */
export function uploadRate(item: UploadItem, now: number) {
  const seconds = (now - item.startedAt) / 1000;
  if (seconds < 2 || item.uploaded <= 0) return null;
  const rate = item.uploaded / seconds;
  return { rate, remainingMs: ((item.size - item.uploaded) / rate) * 1000 };
}
